import {
  parseItemDraggableId,
  parseSectionDraggableId,
  parseSectionDroppableId,
  removeZoneDroppableId,
  sectionOrderDroppableId,
} from './labelDragIds';

import type { DropResult } from '@hello-pangea/dnd';
import type { LabelKind } from '@erikdakoda/label/getLabelKind';
import type { LabelSidebarCallbacks, LabelSidebarItem, LabelSidebarSection } from '../types';

export type RouteLabelDragEndOptions = LabelSidebarCallbacks & {
  /** Dnd type of this sidebar's item drags. */
  droppableType: string;
  /** Suffix of this instance's section-order and remove-zone droppable ids. */
  dragScope?: string;
  /** Every section, including auto sections currently hidden because they are empty. */
  sections: readonly LabelSidebarSection[];
  /** Sections actually rendered, in render order — dnd indices refer to these. */
  visibleSections: readonly LabelSidebarSection[];
  /** Alt/Option state captured during the drag. */
  copyModifierActive: boolean;
  /** Forces a move for sidebars where a row cannot belong to two sections (todos). */
  copyDisabled?: boolean;
};

/**
 * Decides whether a drop copies instead of moves.
 *
 * Copy applies when the destination is the secondary (Favorites) kind, when the drag started in an
 * auto section (the item was never really "in" it), or when Alt/Option is held. Returns `false`
 * when there is no droppable destination (`null` or `auto`) — auto sections never accept drops.
 * @param context Source kind, destination kind, and the modifier state.
 * @param context.sourceKind Kind of the section the drag started in.
 * @param context.destinationKind Kind of the hovered destination section.
 * @param context.copyModifierActive Whether Alt/Option is held.
 * @returns `true` when the drop should copy.
 */
export function shouldCopyLabelItem(context: {
  sourceKind: LabelKind | null;
  destinationKind: LabelKind | null;
  copyModifierActive: boolean;
}): boolean {
  if (context.destinationKind === null || context.destinationKind === 'auto') {
    return false;
  }
  if (context.destinationKind === 'secondary') {
    return true;
  }
  if (context.sourceKind === 'auto') {
    return true;
  }
  return context.copyModifierActive;
}

/**
 * Rebuilds the full section order after a visible-list reorder.
 *
 * Hidden (empty auto) sections keep their relative position: the moved section is spliced in front
 * of whichever visible section now follows it.
 * @param allSectionIds Every section id in its current order.
 * @param visibleSectionIds Rendered section ids in their current order.
 * @param sectionId Section that moved.
 * @param destIndex Its new index within the visible list.
 * @returns Every section id in the new order.
 */
export function reorderSectionIds(
  allSectionIds: readonly string[],
  visibleSectionIds: readonly string[],
  sectionId: string,
  destIndex: number,
): string[] {
  const visibleWithout = visibleSectionIds.filter((id) => id !== sectionId);
  const anchorId = visibleWithout[destIndex];
  const fullWithout = allSectionIds.filter((id) => id !== sectionId);
  const insertAt = anchorId === undefined ? fullWithout.length : fullWithout.indexOf(anchorId);
  fullWithout.splice(insertAt === -1 ? fullWithout.length : insertAt, 0, sectionId);
  return fullWithout;
}

function findItem(
  sections: readonly LabelSidebarSection[],
  sectionId: string | null,
  itemId: string,
): LabelSidebarItem | undefined {
  if (sectionId) {
    const section = sections.find((candidate) => candidate.id === sectionId);
    const inSection = section?.items.find((item) => item.id === itemId);
    if (inSection) {
      return inSection;
    }
  }
  for (const section of sections) {
    const item = section.items.find((candidate) => candidate.id === itemId);
    if (item) {
      return item;
    }
  }
  return undefined;
}

/**
 * Routes one drop for a label sidebar.
 *
 * The sidebar claims a drop only by its **destination** — never by its source — so drags that
 * start in the sidebar and end somewhere else (a prompt dropped into a bot editor) fall through to
 * the page's own routing.
 *
 * Mutation callbacks are started but **not awaited**: page-level `onDragEnd` handlers often `await`
 * this router, and waiting on the network would freeze the drag overlay until the API returns.
 * Callbacks should apply optimistic cache updates synchronously before their first `await`.
 * @param result Drop result from `DragDropContext`.
 * @param options Sidebar sections and mutation callbacks.
 * @returns `true` when this sidebar handled the drop.
 */
export async function routeLabelDragEnd(
  result: DropResult,
  options: RouteLabelDragEndOptions,
): Promise<boolean> {
  const { source, destination, draggableId } = result;
  if (!destination) {
    return false;
  }
  const { droppableType, sections, visibleSections, copyModifierActive } = options;
  const dragScope = options.dragScope ?? droppableType;
  const destinationId = destination.droppableId;

  if (destinationId === sectionOrderDroppableId(dragScope)) {
    const sectionId = parseSectionDraggableId(draggableId);
    if (!sectionId || !sections.some((section) => section.id === sectionId)) {
      return false;
    }
    if (source.droppableId === destinationId && source.index === destination.index) {
      return true;
    }
    const sectionIds = reorderSectionIds(
      sections.map((section) => section.id),
      visibleSections.map((section) => section.id),
      sectionId,
      destination.index,
    );
    void options.onMoveSection({ sectionId, sectionIds });
    return true;
  }

  if (destinationId === removeZoneDroppableId(dragScope)) {
    const itemId = parseItemDraggableId(droppableType, draggableId);
    const sourceSectionId = parseSectionDroppableId(source.droppableId);
    if (!itemId || !sourceSectionId) {
      return true;
    }
    const section = sections.find((candidate) => candidate.id === sourceSectionId);
    const item = findItem(sections, sourceSectionId, itemId);
    if (!section || !item || section.kind === 'auto') {
      return true;
    }
    void options.onRemoveItem({ item, section });
    return true;
  }

  const destSectionId = parseSectionDroppableId(destinationId);
  if (!destSectionId) {
    return false;
  }
  const destSection = sections.find((section) => section.id === destSectionId);
  if (!destSection) {
    return false;
  }
  const itemId = parseItemDraggableId(droppableType, draggableId);
  if (!itemId) {
    return false;
  }
  if (!destSection.droppable) {
    return true;
  }
  if (source.droppableId === destinationId && source.index === destination.index) {
    return true;
  }

  const sourceSectionId = parseSectionDroppableId(source.droppableId);
  const sourceSection = sourceSectionId
    ? sections.find((section) => section.id === sourceSectionId)
    : undefined;
  const item = findItem(sections, sourceSectionId, itemId);
  if (!item) {
    return true;
  }

  const willCopy =
    !options.copyDisabled &&
    shouldCopyLabelItem({
      sourceKind: sourceSection?.kind ?? null,
      destinationKind: destSection.kind,
      copyModifierActive,
    });

  if (willCopy) {
    void options.onCopyItem({
      item,
      sourceSectionId: sourceSectionId ?? null,
      destSection,
      destOrd: destination.index,
    });
    return true;
  }

  if (!sourceSection) {
    return true;
  }
  void options.onMoveItem({
    item,
    sourceSectionId: sourceSection.id,
    sourceOrd: source.index,
    destSectionId: destSection.id,
    destOrd: destination.index,
  });
  return true;
}
